import React, { createContext, useContext } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';

const SettingsContext = createContext(null);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

/**
 * Settings Context Provider
 * Manages all UI settings: theme, dark mode, layout preferences
 */
export const SettingsProvider = ({ children }) => {
  // Theme settings
  const [theme, setTheme] = useLocalStorage('theme', 'forest');
  const [darkMode, setDarkMode] = useLocalStorage('darkMode', false);

  // Layout preferences
  const [verticalLayout, setVerticalLayout] = useLocalStorage('verticalLayout', false);
  const [abbreviatedWeekView, setAbbreviatedWeekView] = useLocalStorage('abbreviatedWeekView', true);
  const [showRunEmojis, setShowRunEmojis] = useLocalStorage('showRunEmojis', false);
  const [weekStartsMonday, setWeekStartsMonday] = useLocalStorage('weekStartsMonday', true);

  // Distance and display preferences
  const [c25kViewMode, setC25kViewMode] = useLocalStorage('c25kViewMode', 'time');
  const [distancePresets, setDistancePresets] = useLocalStorage('distancePresets', [3, 6, 10]);
  const [restDayIcon, setRestDayIcon] = useLocalStorage('restDayIcon', 'sleep');

  // Feature toggles
  const [scoutEnabled, setScoutEnabled] = useLocalStorage('scoutEnabled', true);
  const [autoHideEmptyWeeks, setAutoHideEmptyWeeks] = useLocalStorage('autoHideEmptyWeeks', false);

  // Theme configuration
  const themes = {
    blue: {
      primary: 'bg-sky-500',
      hover: 'hover:bg-sky-600',
      light: 'bg-sky-100',
      lightText: 'text-sky-800',
      text: 'text-sky-500',
      grad: 'from-sky-400 to-sky-600'
    },
    meadow: {
      primary: 'bg-emerald-500',
      hover: 'hover:bg-emerald-600',
      light: 'bg-emerald-100',
      lightText: 'text-emerald-800',
      text: 'text-emerald-500',
      grad: 'from-emerald-400 to-emerald-600'
    },
    amber: {
      primary: 'bg-amber-600',
      hover: 'hover:bg-amber-700',
      light: 'bg-amber-100',
      lightText: 'text-amber-900',
      text: 'text-amber-600',
      grad: 'from-amber-500 to-orange-600'
    },
    forest: {
      primary: 'bg-emerald-700',
      hover: 'hover:bg-emerald-800',
      light: 'bg-emerald-100',
      lightText: 'text-emerald-900',
      text: 'text-emerald-700',
      grad: 'from-emerald-600 to-lime-600'
    },
    hazelnut: {
      primary: 'bg-yellow-700',
      hover: 'hover:bg-yellow-800',
      light: 'bg-yellow-100',
      lightText: 'text-yellow-900',
      text: 'text-yellow-700',
      grad: 'from-yellow-600 to-amber-700'
    },
    sunset: {
      primary: 'bg-orange-500',
      hover: 'hover:bg-orange-600',
      light: 'bg-orange-100',
      lightText: 'text-orange-800',
      text: 'text-orange-500',
      grad: 'from-orange-400 to-pink-500'
    },
    ocean: {
      primary: 'bg-teal-600',
      hover: 'hover:bg-teal-700',
      light: 'bg-teal-100',
      lightText: 'text-teal-900',
      text: 'text-teal-600',
      grad: 'from-teal-500 to-blue-700'
    }
  };

  const t = themes[theme] || themes.blue;

  const value = {
    // Theme
    theme,
    setTheme,
    darkMode,
    setDarkMode,
    t, // Current theme object
    themes, // All available themes

    // Layout
    verticalLayout,
    setVerticalLayout,
    abbreviatedWeekView,
    setAbbreviatedWeekView,
    showRunEmojis,
    setShowRunEmojis,
    weekStartsMonday,
    setWeekStartsMonday,

    // Display preferences
    c25kViewMode,
    setC25kViewMode,
    distancePresets,
    setDistancePresets,
    restDayIcon,
    setRestDayIcon,

    // Feature toggles
    scoutEnabled,
    setScoutEnabled,
    autoHideEmptyWeeks,
    setAutoHideEmptyWeeks,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
